import { useState, useCallback } from 'react'
import type { TableSortState } from './useTableSettings'

export interface SavedFilterSet {
  id: string
  name: string
  createdAt: string
  settings: {
    sort: TableSortState
    filters: Record<string, string | string[]>
    visibleColumns?: Record<string, boolean>
    filterLabels?: Record<string, Record<string, string>>
  }
}

const getStorageKey = (tableId: string) => `table-filter-sets-${tableId}`

const loadFilterSets = (tableId: string): SavedFilterSet[] => {
  try {
    const item = window.localStorage.getItem(getStorageKey(tableId))
    if (item) {
      return JSON.parse(item)
    }
  } catch (error) {
    console.warn(`Error reading filter sets for "${tableId}":`, error)
  }
  return []
}

const persistFilterSets = (tableId: string, sets: SavedFilterSet[]): void => {
  try {
    window.localStorage.setItem(getStorageKey(tableId), JSON.stringify(sets))
  } catch (error) {
    console.warn(`Error saving filter sets for "${tableId}":`, error)
  }
}

export interface UseFilterSetsReturn {
  filterSets: SavedFilterSet[]
  saveFilterSet: (
    name: string,
    sort: TableSortState,
    filters: Record<string, string | string[]>,
    visibleColumns: Record<string, boolean>,
    filterLabels: Record<string, Record<string, string>>,
  ) => void
  deleteFilterSet: (id: string) => void
}

export const useFilterSets = (tableId: string): UseFilterSetsReturn => {
  const [filterSets, setFilterSets] = useState<SavedFilterSet[]>(() => loadFilterSets(tableId))

  const saveFilterSet = useCallback((
    name: string,
    sort: TableSortState,
    filters: Record<string, string | string[]>,
    visibleColumns: Record<string, boolean>,
    filterLabels: Record<string, Record<string, string>>,
  ) => {
    setFilterSets(prev => {
      const newSet: SavedFilterSet = {
        id: crypto.randomUUID(),
        name,
        createdAt: new Date().toISOString(),
        settings: { sort, filters, visibleColumns, filterLabels },
      }
      const updated = [...prev, newSet]
      persistFilterSets(tableId, updated)
      return updated
    })
  }, [tableId])

  const deleteFilterSet = useCallback((id: string) => {
    setFilterSets(prev => {
      const updated = prev.filter(s => s.id !== id)
      persistFilterSets(tableId, updated)
      return updated
    })
  }, [tableId])

  return { filterSets, saveFilterSet, deleteFilterSet }
}
